import * as Sentry from '@sentry/react'
import { User as SentryUser } from '@sentry/react'

import { AuthUser__UserFragment as User } from '@/graphql/api/generated'

export const SentryService = {
  setUser(user: User): void {
    const sentryUser: SentryUser = {
      email: user.email,
      id: user.id,
      username: user.slug,
    }

    Sentry.setUser(sentryUser)

    Sentry.setContext('user_details', {
      country: user.country,
      city: user.city,
      createdAt: user.createdAt,
      fullname: user.fullname,
      gender: user.gender,
      language: user.settings?.language,
      circles: user.circleMemberships?.edges
        ?.map((edge) => edge.node!.circle.name)
        .join(', '),
      geo: {
        city: user.city,
        country_code: `${user.phoneNumber?.countryCode}`,
      },
    })
  },

  clearUser(): void {
    Sentry.setUser(null)
  },

  setContext(key: string, context: Record<string, any>): void {
    Sentry.setContext(key, context)
  },

  addBreadcrumb(breadcrumb: Sentry.Breadcrumb): void {
    Sentry.addBreadcrumb(breadcrumb)
  },

  captureException(error: Error): void {
    Sentry.captureException(error)
  },

  captureMessage(message: string, level?: Sentry.SeverityLevel): void {
    Sentry.captureMessage(message, level)
  },

  captureWarning(message: string, extra?: Record<string, unknown>): void {
    if (extra !== undefined && Object.keys(extra).length > 0) {
      Sentry.captureMessage(message, { level: 'warning', extra })
      return
    }
    Sentry.captureMessage(message, 'warning')
  },

  showFeedbackDialog(): void {
    // Try to get the feedback integration first
    const feedback = Sentry.getFeedback()
    if (feedback) {
      try {
        // Create the widget (this adds the trigger button to the DOM)
        const widget = feedback.createWidget() as { open?: () => void }

        // Auto-click the trigger button after a short delay to open the modal
        setTimeout(() => {
          // Find and click the Sentry feedback trigger button
          const triggerButton = document.querySelector(
            '[data-sentry-feedback], .sentry-feedback-button, #sentry-feedback'
          ) as HTMLElement

          if (triggerButton) {
            triggerButton.click()
          } else if (widget && typeof widget.open === 'function') {
            widget.open()
          }
        }, 100)
      } catch {
        // Fallback to legacy report dialog
        Sentry.showReportDialog()
      }
    } else {
      // Use the legacy report dialog
      Sentry.showReportDialog()
    }
  },
}
